import React from "react";
import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { useSiteContent } from "@/lib/site-content";

export default function ContactMap() {
  const { content } = useSiteContent();
  const c = content.contact;
  const hours = content.hours;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32 bg-[#F5ECE1]"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-8">
            <div>
              <span className="chip">Come find us</span>
              <h2 className="mt-5 font-display font-black tracking-tighter text-4xl md:text-5xl leading-[0.95]">
                Up the stairs on <span className="text-[#E53935]">George Street</span>.
              </h2>
            </div>

            <InfoCard
              icon={MapPin}
              title="Address"
              testid="contact-address"
              lines={[c.address_line1, `${c.address_line2}, ${c.postcode}`, c.country]}
            />

            <InfoCard
              icon={Phone}
              title="Call to order"
              testid="contact-phone"
              lines={[c.phone]}
              href={`tel:${(c.phone || "").replace(/\s/g, "")}`}
            />

            <InfoCard
              icon={Mail}
              title="Email"
              testid="contact-email"
              lines={[c.email]}
              href={`mailto:${c.email}`}
            />

            <div
              data-testid="contact-hours"
              className="rounded-2xl bg-white border border-[#E8DED5] p-6"
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#E53935]/10 grid place-items-center text-[#E53935]">
                  <Clock className="h-5 w-5" />
                </div>
                <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#5C534D]">
                  Opening hours
                </div>
              </div>
              <dl className="mt-5 space-y-3">
                {hours.map((h, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-3 pb-3 border-b border-[#E8DED5] last:border-0 last:pb-0"
                  >
                    <dt className="font-display font-semibold text-sm">{h.day}</dt>
                    <dd
                      className={`text-sm font-semibold tabular-nums ${
                        h.closed ? "text-[#E53935]" : "text-[#1A1A1A]"
                      }`}
                    >
                      {h.time}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative rounded-3xl overflow-hidden border border-[#E8DED5] h-[480px] lg:h-full min-h-[520px] bg-white">
              <iframe
                src={c.maps_embed}
                title="Map to Thai Noodle & Rice Chinatown MCR"
                data-testid="contact-map"
                className="absolute inset-0 h-full w-full"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoCard({ icon: Icon, title, lines, href, testid }) {
  const Wrapper = href ? "a" : "div";
  const extraProps = href ? { href } : {};
  return (
    <Wrapper
      {...extraProps}
      data-testid={testid}
      className="block rounded-2xl bg-white border border-[#E8DED5] p-6 lift"
    >
      <div className="flex items-start gap-4">
        <div className="h-11 w-11 rounded-full bg-[#E53935]/10 grid place-items-center text-[#E53935] shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#5C534D]">
            {title}
          </div>
          <div className="mt-2 font-display font-semibold text-[17px] leading-snug">
            {lines.filter(Boolean).map((l, i) => (
              <div key={i}>{l}</div>
            ))}
          </div>
        </div>
      </div>
    </Wrapper>
  );
}
